/**
 * classAssignments.js — the School OS client on the child's device (institutional path only).
 * ONE source for school priority (GET /api/school/learners/{learnerId}/today: REQUIRED → REVIEW → own picks),
 * ONE evidence write (POST /api/school/evidence → server Evidence Ledger → Mastery Service). The device keeps a
 * short session cache for UX; the server is the institutional record. link.member_id IS the durable learner id.
 */
import { useEffect, useState } from "react";
import { getTeacherLink } from "@/lib/teacherLink";
import { subjectNameFromCode } from "@/lib/catalog";
import { track } from "@/lib/track";

// The membership device id proves this device joined the class; stable per device (same id the join used).
const deviceKeyFor = (link) => link?.device_id || (() => { try { return localStorage.getItem("we_teacher_device_id") || ""; } catch { return ""; } })();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "mwq_class_assignments_v1";
const TTL = 30e3; // short: Today must reflect server evidence/mastery promptly (server is the authority; this only dedupes navigation refetches)

const fromToday = (c) => ({ id: c.assignmentId, code: c.objectiveId, title: c.title, to: c.to, subject: subjectNameFromCode(c.objectiveId), note: "", source: "teacher", state: c.state, decision: c.decision, why: c.why });

export async function fetchClassAssignments({ force = false } = {}) {
  const link = getTeacherLink();
  if (!link?.member_id) return [];
  try {
    const c = JSON.parse(sessionStorage.getItem(KEY) || "null");
    if (!force && c && c.member === link.member_id && Date.now() - c.at < TTL) return c.items;
  } catch { /* ignore */ }
  // School priority order comes from ONE server engine: REQUIRED → REVIEW (→ child's own picks handled by Today itself).
  const t = await fetch(`${API}/school/learners/${link.member_id}/today`, { headers: { "X-Device-Key": deviceKeyFor(link) } }).then((x) => (x.ok ? x.json() : null)).catch(() => null);
  const items = t ? [...(t.required || []).map((c) => ({ ...fromToday(c), kind: "required" })), ...(t.review || []).map((c) => ({ ...fromToday(c), kind: "review" }))] : [];
  try { sessionStorage.setItem(KEY, JSON.stringify({ member: link.member_id, at: Date.now(), items })); } catch { /* ignore */ }
  return items;
}

/** Newest active teacher assignment for this device's linked learner (null when no class link). */
export function useTeacherAssignment() {
  const [item, setItem] = useState(null);
  // Stale-while-revalidate: paint the cached school item at once, then confirm against the server (the authority).
  useEffect(() => {
    let on = true;
    fetchClassAssignments().then((l) => { if (on) setItem(l[0] || null); }).catch(() => {});
    fetchClassAssignments({ force: true }).then((l) => { if (on) setItem((prev) => (JSON.stringify(prev) === JSON.stringify(l[0] || null) ? prev : l[0] || null)); }).catch(() => {});
    return () => { on = false; };
  }, []);
  return item;
}

/** ONE evidence write for a linked school learner. No link → null (Free MWQ stays local-first). */
export async function recordSchoolEvidence({ code, sourceType = "PRACTICE", score, total, result, activityId = null, assignmentId = null }) {
  const link = getTeacherLink();
  if (!link?.member_id || !code) return null;
  const res = result || (total ? { score, total, pct: Math.round((100 * score) / total) } : {});
  const r = await fetch(`${API}/school/evidence`, { method: "POST", headers: { "Content-Type": "application/json", "X-Device-Key": deviceKeyFor(link) },
    body: JSON.stringify({ learnerId: link.member_id, objectiveId: code, sourceType, result: res, activityId, assignmentId, createdBy: "learner" }) }).catch(() => null);
  if (!r?.ok) return null;
  const row = await r.json();
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
  track("school_evidence_recorded", { code, sourceType, state: row?.mastery?.state });
  return row;
}

/** After an exit check: record the outcome on the assignment that targets this objective (else plain CHECK evidence). */
export async function recordAssignmentOutcome(code, score, total) {
  const link = getTeacherLink();
  if (!link?.member_id || !code || !total) return null;
  const items = await fetchClassAssignments().catch(() => []);
  const a = items.find((x) => x.code === code);
  if (!a) return recordSchoolEvidence({ code, sourceType: "CHECK", score, total });
  const r = await fetch(`${API}/teacher/members/${link.member_id}/assignments/${a.id}/outcome`, { method: "POST", headers: { "Content-Type": "application/json", "X-Device-Key": deviceKeyFor(link) }, body: JSON.stringify({ score, total }) });
  if (!r.ok) return null;
  const row = await r.json();
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
  track("assignment_reassessed", { code, state: row.state });
  return row;
}
